import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { userQuery, userQueryVariables } from '../generated/userQuery';
import { userFieldsFragment } from '../models/User';
import { mainApiConfig } from '../../../configure/mainApiConfig';

/**
 * Get a User from the store.
 * @param id
 * @param options
 */
export function useUser(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<userQuery> {
    // Query the data we need from the API.
    const [load, { data, loading, error, refetch }] = useLazyQuery<userQuery, userQueryVariables>(
        gql`
        query userQuery ($id: ID!) {
            model: user(id: $id) {
                ...userFields
            }
        }

        ${userFieldsFragment}
        `,
        {
            variables: { id: id ?? Guid.empty },
            pollInterval: options.pollInterval,
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
